import React, { useState, useEffect, useRef } from "react";
import Typography from "../../components/Typography/Typography";
import { getTimeLeftLabel } from "../../utils/timeUtils.js";
import {
  CircleHelp,
  FilePen,
  List,
  Plus,
  ThumbsUp,
  ClockAlert,
  Trash,
  Clock8,
} from "lucide-react";
import SingleCheckBox from "../../components/SingleCheckBox/SingleCheckBox";
import Button from "../../components/Button/Button.jsx";
import Lottie from "react-lottie";
import confettiAnimation from "../../Icons/confetti.json";
import toast from "react-hot-toast";

const WeekCard = ({ weekData, isLast, updateRoadmap }) => {
  // State for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [clickPrevented, setClickPrevented] = useState(false);

  // Flag to track if task completion is happening right now
  const [justCompletedWeek, setJustCompletedWeek] = useState(false);

  // Initialize tasks from weekData, filtering out empty tasks
  const [tasks, setTasks] = useState(
    Array.isArray(weekData?.tasks) 
      ? weekData.tasks.filter(task => task.titolo.trim() !== "") 
      : []
  );

  // Checkbox states
  const [checkboxStates, setCheckboxStates] = useState(
    tasks.reduce((acc, task, index) => {
      acc[index] = task.completato || false;
      return acc;
    }, {})
  );

  const completedTasks = Object.values(checkboxStates).filter(
    (value) => value
  ).length;
  const totalTasks = tasks.length;
  const taskLeft = totalTasks - completedTasks;

  // Effect to handle confetti display when justCompletedWeek is set to true
  useEffect(() => {
    if (justCompletedWeek) {
      setShowConfetti(true);

      // Reset the justCompletedWeek flag and hide confetti after 3 seconds
      const timer = setTimeout(() => {
        setShowConfetti(false);
        setJustCompletedWeek(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [justCompletedWeek]);

  // Confetti animation options
  const confettiOptions = {
    loop: false,
    autoplay: true,
    animationData: confettiAnimation,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  // Function to update checkbox state
  const handleCheckboxChange = (index) => {
    // Prevent handling if this is part of a drag operation
    if (clickPrevented) return;

    // Calculate if this checkbox action will complete the week
    const currentCheckboxValue = checkboxStates[index];
    const willCompleteWeek =
      !currentCheckboxValue &&
      completedTasks === totalTasks - 1 &&
      totalTasks > 0;

    const newState = {
      ...checkboxStates,
      [index]: !currentCheckboxValue,
    };

    setCheckboxStates(newState);

    // Update the original tasks array
    const updatedTasks = tasks.map((task, i) => {
      if (i === index) {
        return {
          ...task,
          completato: !currentCheckboxValue,
        };
      }
      return task;
    });

    // Update local state
    setTasks(updatedTasks);

    // Call the update function if provided
    if (updateRoadmap && weekData !== "first") {
      updateRoadmap(weekData.settimana, updatedTasks);
      toast.success("Roadmap aggiornata con successo");
    }

    // If this action just completed the week, trigger the confetti
    if (willCompleteWeek) {
      setJustCompletedWeek(true);
    }
  };

  // Function to toggle edit mode
  const toggleEditMode = () => {
    if (isEditMode) {
      // Remove empty tasks before saving
      const nonEmptyTasks = tasks.filter(task => task.titolo.trim() !== "");
      
      // Update tasks state with non-empty tasks
      setTasks(nonEmptyTasks);
      
      // Update checkbox states to match the filtered tasks
      const newCheckboxStates = {};
      nonEmptyTasks.forEach((task, index) => {
        newCheckboxStates[index] = task.completato || false;
      });
      setCheckboxStates(newCheckboxStates);
      
      // Saving changes
      if (updateRoadmap && weekData !== "first") {
        updateRoadmap(weekData.settimana, nonEmptyTasks);
        toast.success("Roadmap aggiornata con successo");
      }
    }
    setIsEditMode(!isEditMode);
  };

  // Function to handle task text changes
  const handleTaskTextChange = (index, newText) => {
    const updatedTasks = [...tasks];
    updatedTasks[index] = {
      ...updatedTasks[index],
      titolo: newText,
    };
    setTasks(updatedTasks);
  };

  // Function to delete task
  const deleteTask = (index) => {
    const updatedTasks = tasks.filter((_, i) => i !== index);
    setTasks(updatedTasks);

    // Update checkbox states
    const newCheckboxStates = {};
    Object.keys(checkboxStates).forEach((key) => {
      const keyNum = parseInt(key);
      if (keyNum < index) {
        newCheckboxStates[keyNum] = checkboxStates[keyNum];
      } else if (keyNum > index) {
        newCheckboxStates[keyNum - 1] = checkboxStates[keyNum];
      }
    });
    setCheckboxStates(newCheckboxStates);

    // If in edit mode, immediately log and update the tasks when deleting
    if (isEditMode && updateRoadmap && weekData !== "first") {
      updateRoadmap(weekData.settimana, updatedTasks);
      toast.success("Task eliminata con successo");
    }
  };

  // Function to add new task
  const addNewTask = () => {
    const newTask = {
      titolo: "",
      completato: false,
    };
    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);

    // Add new checkbox state
    setCheckboxStates((prevState) => ({
      ...prevState,
      [tasks.length]: false,
    }));

    // For adding new tasks, we don't update the backend until the user actually enters content
  };

  // Update clickPrevented when the parent's isDragging state changes
  useEffect(() => {
    // This will be triggered by the parent component's drag state
    const handleDragChange = (e) => {
      if (e.detail.isDragging) {
        setClickPrevented(true);
      } else {
        // Reset after a small delay to prevent immediate checkbox clicks after drag
        setTimeout(() => {
          setClickPrevented(false);
        }, 100);
      }
    };

    window.addEventListener("dragStateChange", handleDragChange);

    return () => {
      window.removeEventListener("dragStateChange", handleDragChange);
    };
  }, []);

  // Refs for auto-resizing textareas
  const textareaRefs = useRef({});

  // Function to auto-resize textareas
  const autoResizeTextarea = (index) => {
    const textarea = textareaRefs.current[index];
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // Effect to resize textareas when tasks change or edit mode is enabled
  useEffect(() => {
    if (isEditMode) {
      tasks.forEach((_, index) => {
        autoResizeTextarea(index);
      });
    }
  }, [tasks, isEditMode]);

  // Effect to check for and remove empty tasks when exiting edit mode
  useEffect(() => {
    if (!isEditMode && tasks.some(task => task.titolo.trim() === "")) {
      const filteredTasks = tasks.filter(task => task.titolo.trim() !== "");
      
      // Only update if we actually removed some tasks
      if (filteredTasks.length !== tasks.length) {
        setTasks(filteredTasks);
        
        // Reset checkbox states
        const newCheckboxStates = {};
        filteredTasks.forEach((task, index) => {
          newCheckboxStates[index] = task.completato || false;
        });
        setCheckboxStates(newCheckboxStates);
        
        // Update the roadmap if needed
        if (updateRoadmap && weekData !== "first") {
          updateRoadmap(weekData.settimana, filteredTasks);
        }
      }
    }
  }, [isEditMode]);

  const isMobile = window.innerWidth < 768;

  const getDaysLeftColor = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return "bg-red-500"; // Rosso se è già scaduta
    } else if (diffDays <= 4) {
      return "bg-yellow-400"; // Giallo se mancano dal minuto ai 2 giorni
    } else {
      return "bg-white border-2 border-150"; // Bianco se mancano più di 2 giorni
    }
  };

  // Funzione per selezionare l'icona appropriata in base allo stato
  const getStatusIcon = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const isWhiteBg =
      getDaysLeftColor(endDate) === "bg-white border-2 border-150";
    const iconColor = isWhiteBg ? "text-black" : "text-white";

    if (diffDays < 0) {
      // Scaduta
      return <ClockAlert size={20} className={iconColor} />;
    } else if (diffDays <= 4) {
      // Urgente
      return <Clock8 size={20} className={iconColor} />;
    } else {
      // Normale
      return <List size={20} className={iconColor} />;
    }
  };

  const getTaskMessageByStatus = (endDate, taskCount) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      // Scaduta - formato alert
      return `Attenzione! Hai ${taskCount} task non ${
        taskCount === 1 ? "completata" : "completate"
      }`;
    } else if (diffDays <= 2) {
      // Urgente
      return `Hai ancora ${taskCount} task da completare, ma il tempo stringe!`;
    } else {
      // Normale
      return `Hai ancora ${taskCount} task da completare`;
    }
  };

  // Filter tasks in render to ensure no empty tasks are displayed in non-edit mode
  const displayTasks = isEditMode 
    ? tasks 
    : tasks.filter(task => task.titolo.trim() !== "");

  return (
    <div className="flex flex-col md:flex-row items-top">
      {showConfetti && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <Lottie options={confettiOptions} />
        </div>
      )}

      <div className="bg-gray-100 rounded-[12px] border-2 border-gray-200 flex-shrink-0 w-full md:w-96 h-auto md:h-144 overflow-hidden overflow-y-scroll scrollbar-hidden">
        <div className="flex flex-col gap-3 p-6">
          <div className="flex flex-col gap-1">
            <Typography size="12" weight="md" color="black">
              {weekData === "first"
                ? "Introduzione"
                : `Settimana ${weekData.settimana}`}
            </Typography>
            <Typography
              truncate={false}
              family="serif"
              size="28"
              desktopSize="32"
              weight="sb"
              color="black"
            >
              {weekData === "first"
                ? "Qui ha inizio il tuo epico percorso di stesura."
                : weekData.titolo}
            </Typography>
          </div>
          {weekData !== "first" && (
            <div
              className={`flex w-full gap-3 items-center rounded-[8px] p-4 ${
                taskLeft === 0
                  ? "bg-green-500"
                  : getDaysLeftColor(weekData.data_fine)
              }`}
            >
              {taskLeft > 0 ? (
                getStatusIcon(weekData.data_fine)
              ) : (
                <ThumbsUp size={20} className="text-white" />
              )}
              <Typography
                size="12"
                weight="md"
                color={
                  taskLeft > 0
                    ? getDaysLeftColor(weekData.data_fine) ===
                      "bg-white border-2 border-150"
                      ? "black"
                      : "white"
                    : "white"
                }
              >
                {taskLeft > 0
                  ? getTaskMessageByStatus(weekData.data_fine, taskLeft)
                  : "Hai completato tutte le task per tempo!"}
              </Typography>
            </div>
          )}
        </div>

        <div className="flex w-full h-[2px] bg-gray-200" />

        <div className="p-4 flex flex-col gap-4 w-full">
          <Typography size="12" weight="sb" color="gray-600">
            {weekData === "first"
              ? "Come funziona?"
              : `Da completare entro ${getTimeLeftLabel(weekData.data_fine)}`}
          </Typography>

          <div className="flex flex-col gap-3 w-full">
            {weekData === "first" ? (
              <div className="flex flex-col gap-3">
                {[
                  "Athena genererà la tua roadmap per la tesi, ma potrai modificarla ogni settimana per adattarla alle tue esigenze.",
                  "Aggiungi note tra le settimane e personalizza ogni parte della tua roadmap per una gestione ottimale della tua tesi.",
                  "Modifica facilmente i task della tua roadmap: aggiungi, cancella e riorganizza le attività a tuo piacimento.",
                  "Tieni traccia del tuo progresso in tempo reale: la tua roadmap si adatta alle modifiche che fai ogni settimana.",
                ].map((text, index) => (
                  <div
                    key={index}
                    className="flex w-full gap-3 rounded-[8px] p-4 bg-white border-2 border-150 items-center text-gray-600"
                  >
                    <div className="w-5 h-5 shrink-0">
                      <CircleHelp size={20} />
                    </div>
                    <Typography size="12" weight="md">
                      {text}
                    </Typography>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {displayTasks.map((task, index) => (
                  <div
                    key={index}
                    className="flex w-full gap-3 rounded-[8px] p-4 bg-white border-2 border-150 items-center text-gray-600"
                  >
                    {isEditMode ? (
                      <div className="flex w-full justify-between items-start">
                        <div className="flex items-start gap-3 grow">
                          <textarea
                            ref={(el) => (textareaRefs.current[index] = el)}
                            value={task.titolo}
                            placeholder="Inserisci il titolo della task"
                            onChange={(e) => {
                              handleTaskTextChange(index, e.target.value);
                              autoResizeTextarea(index);
                            }}
                            className="grow outline-none bg-transparent text-gray-600 text-[12px] resize-none overflow-hidden min-h-[24px]"
                            rows={1}
                          />
                        </div>
                        <button
                          onClick={() => deleteTask(index)}
                          className="text-error ml-2 flex-shrink-0"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    ) : (
                      <SingleCheckBox
                        checked={checkboxStates[index]}
                        setChecked={() => handleCheckboxChange(index)}
                      >
                        <Typography size="12" weight="md">
                          {task.titolo}
                        </Typography>
                      </SingleCheckBox>
                    )}
                  </div>
                ))}

                {isEditMode && (
                  <Button
                    size="sm"
                    type="bw"
                    width="full"
                    licon={<Plus size={20} />}
                    text="Nuova Task"
                    onClick={addNewTask}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {weekData !== "first" && (
          <>
            <div className="flex w-full h-[2px] bg-gray-200" />

            <div className="p-4">
              {isEditMode ? (
                <Button
                  width="full"
                  type="color"
                  text="Salva"
                  size={isMobile ? "sm" : "md"}
                  onClick={toggleEditMode}
                />
              ) : (
                <Button
                  width="full"
                  type="bw"
                  text="Modifica settimana"
                  size={isMobile ? "sm" : "md"}
                  ricon={<FilePen size={20} />}
                  onClick={toggleEditMode}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WeekCard;